<template>
  <div class="pw-input-parent">
    <input
      class="pw-input"
      :placeholder="placeholder"
      v-on="{
        ...$listeners,
        blur: onBlurHandler,
        input: onInputHandler,
        focus: onFocusHandler,
      }"
      ref="numeric"
      v-model="amount"
      type="text"
      :name="name"
      :readonly="readonly"
      :disabled="disabled"
    />
  </div>
</template>

<script>
export default {
  name: 'InputNumeric',
  $_veeValidate: {
    value() {
      return this.value;
    },
    name() {
      return this.name;
    },
  },
  props: {
    name: {
      type: String,
      default: '',
      required: false,
    },
    max: {
      type: Number,
      default: Number.MAX_SAFE_INTEGER || -9007199254740991,
      // default:10,
      required: false,
    },
    min: {
      type: Number,
      default: Number.MIN_SAFE_INTEGER || -9007199254740991,
      required: false,
    },
    minus: {
      type: Boolean,
      default: false,
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    emptyValue: {
      type: [Number, String],
      default: '',
      required: false,
    },
    decimals: {
      type: Number,
      default: 0,
      required: false,
    },
    separator: {
      type: String,
      default: ',',
      required: false,
    },
    numberFormat: {
      type: String,
      default: 'n2',
    },
    outputType: {
      required: false,
      type: String,
      default: 'Number',
    },
    value: {
      type: [Number, String],
      default: '',
      required: false,
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      amount: 0,
    };
  },
  computed: {
    precision() {
      return this.decimals;
    },
    amountNumber() {
      return this.unformat(this.amount);
    },
    valueNumber() {
      return this.unformat(this.value);
    },
    decimalSeparatorSymbol() {
      const separator = ',';
      return this.separator || separator;
    },
    regexValidator() {
      const minus = this.minus ? '(?!^\\-)' : '';
      return new RegExp(`${minus}([^0-9|\\${this.decimalSeparatorSymbol}])+`);
    },
    avoidMultiSeparator() {
      return new RegExp(`(\\${this.decimalSeparatorSymbol})(?=.*\\1)`);
    },
  },
  watch: {
    value() {
      setTimeout(() => {
        if (this.$refs.numeric !== document.activeElement) {
          this.process(this.valueNumber);
          this.amount = this.format(this.valueNumber);
        }
      }, 10);
    },
    valueNumber(newValue) {
      if (
        this.$refs.numeric !== document.activeElement &&
        typeof newValue === 'number' &&
        !isNaN(newValue)
      ) {
        this.amount = this.format(newValue);
      }
    },
    separator() {
      this.process(this.valueNumber);
      this.amount = this.format(this.valueNumber);
    },
    currency() {
      this.process(this.valueNumber);
      this.amount = this.format(this.valueNumber);
    },
    precision() {
      this.process(this.valueNumber);
      this.amount = this.format(this.valueNumber);
    },
    name() {
      this.changeName();
    },
    required() {
      this.changeRequired();
    },
  },
  mounted() {
    if (!this.placeholder) {
      this.process(this.valueNumber);
      this.amount = this.format(this.valueNumber);
      // In case of delayed props value.
      setTimeout(() => {
        this.process(this.valueNumber);
        this.amount = this.format(this.valueNumber);
      }, 500);
    }

    this.changeName();
    this.changeRequired();
  },
  methods: {
    onBlurHandler(e) {
      this.$emit('blur', e);
      this.amount = this.format(this.valueNumber);
    },
    onFocusHandler(e) {
      this.$emit('focus', e);
      if (this.valueNumber === 0) {
        this.amount = null;
      } else {
        this.amount = this.$formatNumber(
          this.valueNumber,
          this.numberFormat.replace(/['A-z']/, 'n')
        );
      }
    },
    onInputHandler() {
      this.process(this.amountNumber);
    },
    process(value) {
      if (value >= this.max) this.update(this.max);
      if (value <= this.min) this.update(this.min);
      if (value > this.min && value < this.max) this.update(value);
      if (!this.minus && value < 0) {
        if (this.min >= 0) this.update(this.min);
        else this.update(0);
      }
    },
    update(value) {
      const fixedValue = this.$unformatNumber(
        this.$formatNumber(value, this.numberFormat.replace(/['A-z']/, 'n'))
      );
      const output =
        this.outputType.toLowerCase() === 'string'
          ? fixedValue
          : fixedValue !== '' && fixedValue !== null
          ? Number(fixedValue)
          : fixedValue;
      this.$emit('input', output);
    },
    format(value) {
      return this.$formatNumber(value, this.numberFormat);
    },
    unformat(value) {
      const toUnformat = typeof value === 'string' && value === '' ? this.emptyValue : value;
      return this.$unformatNumber(toUnformat);
    },
    changeName() {
      if (this.$parent.changeName) {
        this.$parent.changeName(this.name);
      }
    },
    changeRequired() {
      if (this.$parent.changeRequired) {
        this.$parent.changeRequired(this.required);
      }
    },
  },
};
</script>

<style lang="scss">
.pw-input {
  box-sizing: border-box;
  height: 32px;
  width: 100%;
  border: 1px solid #bdc6cc;
  border-radius: 3px;
  padding-left: 8px;
  font-size: 16px;
  color: #333;
  font-family: 'Nunito Sans', sans-serif;
  outline: none;
  &:focus {
    border-color: #4571d8;
  }
  &::placeholder {
    color: #bdc6cc;
    text-transform: none;
  }
  &:read-only {
    background-color: #e9ecef;
    pointer-events: none !important;
    cursor: not-allowed;
    &:focus {
      border-color: #bdc6cc;
    }
  }
}
.uppercase {
  text-transform: uppercase;
}
.capitalize {
  text-transform: capitalize;
}
.lowercase {
  text-transform: lowercase;
}

.pw-input-parent {
  display: block;
  position: relative;
  width: 100%;
  .clearable {
    position: absolute;
    top: calc(100% / 6);
    right: 5px;
    color: #cccccc;
  }
}
</style>
